import fontkit from "@pdf-lib/fontkit";
import { PDFDocument, PDFFont, PDFPage, rgb } from "pdf-lib";
import type { CramProductOrigin, Customer, KYC } from "../types";

export interface RiskRatingPdfInput {
  customer: Customer;
  kyc?: KYC;
  riskCode?: string;
  riskLabel?: string;
  printedOn?: Date;
}

const PAGE_WIDTH = 595.28;
const PAGE_HEIGHT = 841.89;
const TABLE_X = 24;
const TABLE_TOP = PAGE_HEIGHT - 24;
const TABLE_WIDTH = PAGE_WIDTH - 48;
const LEFT_COL_WIDTH = 240;
const RIGHT_COL_WIDTH = TABLE_WIDTH - LEFT_COL_WIDTH;
const BLACK = rgb(0, 0, 0);

const normalizeText = (value?: string | null): string => {
  if (!value) return "";
  return `${value}`.replace(/\s+/g, " ").trim();
};

const titleCase = (value?: string | null): string => {
  const clean = normalizeText(value).replace(/_/g, " ");
  if (!clean) return "-";

  return clean
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const isGenericRiskLabel = (riskLabel?: string | null, riskCode?: string | null): boolean => {
  const cleanRiskLabel = normalizeText(riskLabel).toLowerCase();
  const cleanRiskCode = normalizeText(riskCode).toLowerCase();

  if (!cleanRiskLabel) return true;
  return cleanRiskLabel === cleanRiskCode || cleanRiskLabel === `${cleanRiskCode} risk`;
};

export const formatRiskRatingPdfDate = (date = new Date()): string =>
  new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);

export const getRiskRatingOverrideReason = ({
  customer,
  kyc,
  riskCode,
  riskLabel,
}: RiskRatingPdfInput): string => {
  const normalizedRiskCode = normalizeText(riskCode).toLowerCase();
  const highRiskReason = normalizeText(kyc?.highriskOverrideReason) || normalizeText(customer.highRiskOverride);
  const lowRiskReason = normalizeText(customer.lowRiskOverride);
  const calculatedRiskReason = isGenericRiskLabel(riskLabel, riskCode) ? "" : normalizeText(riskLabel);

  // CRAM-managed labels: "CRAM override: <reason>" shows just the reason;
  // a generic label with a CRAM snapshot means High from the weighted score
  // alone, shown as "CRAM" (legacy high always carries named reasons).
  const cramOverridePrefix = "cram override:";
  if (calculatedRiskReason.toLowerCase().startsWith(cramOverridePrefix)) {
    return calculatedRiskReason.slice(cramOverridePrefix.length).trim() || "Nil";
  }

  if (normalizedRiskCode === "high") return highRiskReason || calculatedRiskReason || (kyc?.cram ? "CRAM" : "Nil");
  if (normalizedRiskCode === "low") return lowRiskReason || "Nil";

  return highRiskReason || lowRiskReason || calculatedRiskReason || "Nil";
};

const getRiskColor = (riskCode?: string) => {
  const normalizedRiskCode = normalizeText(riskCode).toLowerCase();

  if (normalizedRiskCode === "prohibited") return rgb(0.35, 0, 0.4);
  if (normalizedRiskCode === "high") return rgb(0.88, 0.05, 0);
  if (normalizedRiskCode === "medium") return rgb(0.85, 0.44, 0);
  return rgb(0, 0.63, 0.24);
};

const base64ToBytes = (base64Value: string): Uint8Array => {
  const base64 = base64Value.includes(",") ? base64Value.split(",").pop() || "" : base64Value;
  const binary = window.atob(base64.replace(/\s/g, ""));
  const bytes = new Uint8Array(binary.length);

  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  return bytes;
};

const photoToBytes = (photo?: KYC["photo"]): Uint8Array | null => {
  try {
    if (!photo) return null;
    if (typeof photo === "string") return base64ToBytes(photo);
    if (photo instanceof Uint8Array) return photo;
    if (photo instanceof ArrayBuffer) return new Uint8Array(photo);
  } catch (error) {
    console.warn("Unable to decode risk rating photo", error);
  }

  return null;
};

const isJpeg = (bytes: Uint8Array): boolean =>
  bytes.length > 2 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff;

const embedPhoto = async (pdfDoc: PDFDocument, photo?: KYC["photo"]) => {
  const bytes = photoToBytes(photo);

  if (!bytes) return null;

  try {
    return isJpeg(bytes) ? await pdfDoc.embedJpg(bytes) : await pdfDoc.embedPng(bytes);
  } catch (error) {
    console.warn("Unable to embed risk rating photo", error);
    return null;
  }
};

const embedTextFont = async (pdfDoc: PDFDocument): Promise<PDFFont> => {
  pdfDoc.registerFontkit(fontkit);
  const response = await fetch("/Arial.ttf");

  if (!response.ok) {
    throw new Error("Unable to load PDF font");
  }

  return pdfDoc.embedFont(await response.arrayBuffer(), { subset: true });
};

const splitLongWord = (word: string, font: PDFFont, fontSize: number, maxWidth: number): string[] => {
  const segments: string[] = [];
  let currentSegment = "";

  for (const char of word) {
    const nextSegment = `${currentSegment}${char}`;

    if (font.widthOfTextAtSize(nextSegment, fontSize) <= maxWidth || !currentSegment) {
      currentSegment = nextSegment;
    } else {
      segments.push(currentSegment);
      currentSegment = char;
    }
  }

  if (currentSegment) segments.push(currentSegment);
  return segments;
};

const wrapText = (text: string, font: PDFFont, fontSize: number, maxWidth: number): string[] => {
  const paragraphs = text.split(/\r?\n/);
  const lines: string[] = [];

  paragraphs.forEach((paragraph) => {
    const words = normalizeText(paragraph).split(" ").filter(Boolean);
    let currentLine = "";

    words.forEach((word) => {
      const candidate = currentLine ? `${currentLine} ${word}` : word;

      if (font.widthOfTextAtSize(candidate, fontSize) <= maxWidth) {
        currentLine = candidate;
        return;
      }

      if (currentLine) {
        lines.push(currentLine);
        currentLine = "";
      }

      const wordSegments = splitLongWord(word, font, fontSize, maxWidth);
      lines.push(...wordSegments.slice(0, -1));
      currentLine = wordSegments[wordSegments.length - 1] || "";
    });

    if (currentLine) lines.push(currentLine);
  });

  return lines.length > 0 ? lines : [""];
};

const getFittedText = (
  text: string,
  font: PDFFont,
  maxWidth: number,
  maxHeight: number,
  initialFontSize: number,
  minimumFontSize: number,
) => {
  for (let fontSize = initialFontSize; fontSize >= minimumFontSize; fontSize -= 1) {
    const lineHeight = fontSize * 1.22;
    const lines = wrapText(text, font, fontSize, maxWidth);

    if (lines.length * lineHeight <= maxHeight) {
      return { lines, fontSize, lineHeight };
    }
  }

  const lineHeight = minimumFontSize * 1.22;
  const lines = wrapText(text, font, minimumFontSize, maxWidth);
  const maxLines = Math.max(1, Math.floor(maxHeight / lineHeight));
  const fittedLines = lines.slice(0, maxLines);

  if (lines.length > maxLines && fittedLines.length > 0) {
    const lastLine = fittedLines[fittedLines.length - 1];
    fittedLines[fittedLines.length - 1] = `${lastLine.slice(0, Math.max(0, lastLine.length - 1))}...`;
  }

  return { lines: fittedLines, fontSize: minimumFontSize, lineHeight };
};

const drawCellText = ({
  page,
  text,
  font,
  x,
  y,
  width,
  height,
  fontSize = 17,
  color = BLACK,
  padding = 12,
  bold = false,
}: {
  page: PDFPage;
  text: string;
  font: PDFFont;
  x: number;
  y: number;
  width: number;
  height: number;
  fontSize?: number;
  color?: ReturnType<typeof rgb>;
  padding?: number;
  bold?: boolean;
}) => {
  const maxWidth = width - padding * 2;
  const maxHeight = height - padding * 2;
  const fittedText = getFittedText(text, font, maxWidth, maxHeight, fontSize, 10);
  const totalTextHeight = fittedText.lines.length * fittedText.lineHeight;
  let baselineY = y + height - (height - totalTextHeight) / 2 - fittedText.fontSize;

  fittedText.lines.forEach((line) => {
    const drawOptions = {
      x: x + padding,
      y: baselineY,
      size: fittedText.fontSize,
      font,
      color,
    };

    page.drawText(line, drawOptions);

    if (bold) {
      page.drawText(line, { ...drawOptions, x: drawOptions.x + 0.25 });
    }

    baselineY -= fittedText.lineHeight;
  });
};

const drawTableGrid = (page: PDFPage, tableBottom: number, rowHeights: number[]) => {
  let cursorY = TABLE_TOP;

  page.drawRectangle({
    x: TABLE_X,
    y: tableBottom,
    width: TABLE_WIDTH,
    height: TABLE_TOP - tableBottom,
    borderWidth: 1,
    borderColor: BLACK,
  });

  rowHeights.forEach((rowHeight, index) => {
    cursorY -= rowHeight;

    if (index < rowHeights.length - 1) {
      page.drawLine({
        start: { x: TABLE_X, y: cursorY },
        end: { x: TABLE_X + TABLE_WIDTH, y: cursorY },
        thickness: 1,
        color: BLACK,
      });
    }
  });

  page.drawLine({
    start: { x: TABLE_X + LEFT_COL_WIDTH, y: tableBottom },
    end: { x: TABLE_X + LEFT_COL_WIDTH, y: TABLE_TOP },
    thickness: 1,
    color: BLACK,
  });
};

export const generateRiskRatingPdf = async ({
  customer,
  kyc,
  riskCode,
  riskLabel,
  printedOn = new Date(),
}: RiskRatingPdfInput): Promise<Uint8Array> => {
  const pdfDoc = await PDFDocument.create();
  const page = pdfDoc.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
  const font = await embedTextFont(pdfDoc);
  const photo = await embedPhoto(pdfDoc, kyc?.photo);
  const rightX = TABLE_X + LEFT_COL_WIDTH;
  const printedDate = formatRiskRatingPdfDate(printedOn);
  const displayRiskLevel = titleCase(riskCode || riskLabel);
  const normalizedRiskCode = normalizeText(riskCode).toLowerCase();
  const rows: { label: string; value: string; height: number; color?: ReturnType<typeof rgb> }[] = [
    { label: "Picture", value: "", height: 140 },
    { label: "Customer Name:", value: normalizeText(kyc?.name_en) || normalizeText(customer.name_en) || "-", height: 70 },
    { label: "CIF Number:", value: normalizeText(customer.crmID) || normalizeText(customer.id) || "-", height: 70 },
    { label: "Printed on:", value: printedDate, height: 70 },
    { label: "Risk Level:", value: displayRiskLevel, color: getRiskColor(riskCode || riskLabel), height: 70 },
  ];

  if (normalizedRiskCode !== "medium" && normalizedRiskCode !== "low") {
    rows.push({ label: "Risk Reason:", value: getRiskRatingOverrideReason({ customer, kyc, riskCode, riskLabel }), height: 74 });
  }

  const tableBottom = TABLE_TOP - rows.reduce((total, row) => total + row.height, 0);

  pdfDoc.setTitle(`Risk Status - ${rows[1].value}`);
  pdfDoc.setSubject("Client risk rating card");
  pdfDoc.setCreationDate(new Date());

  drawTableGrid(page, tableBottom, rows.map((row) => row.height));

  let rowTop = TABLE_TOP;

  rows.forEach((row, index) => {
    const rowHeight = row.height;
    const rowBottom = rowTop - rowHeight;

    drawCellText({
      page,
      text: row.label,
      font,
      x: TABLE_X,
      y: rowBottom,
      width: LEFT_COL_WIDTH,
      height: rowHeight,
      fontSize: 17,
      bold: true,
    });

    if (index === 0) {
      if (photo) {
        const maxPhotoWidth = 126;
        const maxPhotoHeight = 126;
        const photoScale = Math.min(maxPhotoWidth / photo.width, maxPhotoHeight / photo.height);
        const photoWidth = photo.width * photoScale;
        const photoHeight = photo.height * photoScale;

        page.drawImage(photo, {
          x: rightX + 20,
          y: rowBottom + (rowHeight - photoHeight) / 2,
          width: photoWidth,
          height: photoHeight,
        });
      } else {
        drawCellText({
          page,
          text: "No picture",
          font,
          x: rightX,
          y: rowBottom,
          width: RIGHT_COL_WIDTH,
          height: rowHeight,
          fontSize: 15,
          color: rgb(0.45, 0.45, 0.45),
        });
      }
    } else {
      drawCellText({
        page,
        text: row.value,
        font,
        x: rightX,
        y: rowBottom,
        width: RIGHT_COL_WIDTH,
        height: rowHeight,
        fontSize: 17,
        color: row.color || BLACK,
        bold: true,
      });
    }

    rowTop = rowBottom;
  });

  return pdfDoc.save();
};

export const openRiskRatingPdf = async (
  input: RiskRatingPdfInput,
  previewWindow?: Window | null,
): Promise<string> => {
  const pdfBytes = await generateRiskRatingPdf(input);
  const pdfArrayBuffer = new ArrayBuffer(pdfBytes.byteLength);
  new Uint8Array(pdfArrayBuffer).set(pdfBytes);
  const pdfUrl = URL.createObjectURL(new Blob([pdfArrayBuffer], { type: "application/pdf" }));

  if (previewWindow && !previewWindow.closed) {
    previewWindow.location.href = pdfUrl;
  } else {
    window.open(pdfUrl, "_blank", "noopener,noreferrer");
  }

  window.setTimeout(() => URL.revokeObjectURL(pdfUrl), 60_000);
  return pdfUrl;
};

// ---------------------------------------------------------------------------
// CRAM breakdown PDF
// ---------------------------------------------------------------------------
//
// Extends the legacy risk-rating card with the full CRAM calculation
// breakdown. Rendered on a portrait A4 layout so the per-category table and the
// reason lists fit. Reuses the private helpers above (font/photo embedding,
// text wrapping, colors). `breakdownRows` is produced by the KYC view's
// `getCramBreakdownRows(kyc)` so the mapping logic is not duplicated here.

// Mirrors the shape returned by `getCramBreakdownRows` in customer_kyc.tsx.
export interface CramPdfBreakdownRow {
  category: string;
  label: string;
  input: string;
  chosenKey: string;
  candidates: { key: string; score: number; chosen: boolean; origin?: CramProductOrigin }[];
  score: number;
  weighted: number;
  mandatory: boolean;
  status: "used" | "missing" | "unscored" | "none";
}

export interface CramRiskPdfInput extends RiskRatingPdfInput {
  breakdownRows: CramPdfBreakdownRow[];
}

const CRAM_PAGE_WIDTH = 595.28;
const CRAM_PAGE_HEIGHT = 841.89;
const CRAM_MARGIN = 36;
const CRAM_CONTENT_WIDTH = CRAM_PAGE_WIDTH - CRAM_MARGIN * 2;
const GREY = rgb(0.42, 0.42, 0.42);
const LIGHT_LINE = rgb(0.8, 0.8, 0.8);

const cramStatusText = (row: CramPdfBreakdownRow): string => {
  if (row.category === "directHigh") {
    return row.status === "used" ? "Flags High" : "No effect";
  }
  switch (row.status) {
    case "missing":
      return "Missing";
    case "unscored":
      return "Not scored";
    case "used":
      return "Used";
    default:
      return "Not provided";
  }
};

// One line per candidate, so the PDF shows everything the engine weighed for
// this category rather than only the winner. AML needs to see the values that
// lost as well: a rating is only defensible if the alternatives are visible.
// Mirrors the AML breakdown modal on the KYC page.
//
// Direct High has no candidate keys, so it falls back to `row.input` and its
// friendly reason labels.
const cramRowInputLines = (row: CramPdfBreakdownRow): string[] => {
  if (row.candidates.length === 0) return [row.input || "-"];
  // Highest first, so the value that set the category score reads first.
  const sorted = [...row.candidates].sort((a, b) => b.score - a.score);
  return sorted.map((c) => {
    const marks = [
      c.chosen ? "used" : null,
      c.origin === "requested" ? "requested" : null,
    ].filter(Boolean);
    return `${c.key} (${c.score})${marks.length ? ` — ${marks.join(", ")}` : ""}`;
  });
};

export const generateCramRiskPdf = async ({
  customer,
  kyc,
  riskCode,
  riskLabel,
  breakdownRows,
  printedOn = new Date(),
}: CramRiskPdfInput): Promise<Uint8Array> => {
  const pdfDoc = await PDFDocument.create();
  const font = await embedTextFont(pdfDoc);
  const photo = await embedPhoto(pdfDoc, kyc?.photo);
  const cram = kyc?.cram;

  let page = pdfDoc.addPage([CRAM_PAGE_WIDTH, CRAM_PAGE_HEIGHT]);
  let cursorY = CRAM_PAGE_HEIGHT - CRAM_MARGIN;

  const newPage = () => {
    page = pdfDoc.addPage([CRAM_PAGE_WIDTH, CRAM_PAGE_HEIGHT]);
    cursorY = CRAM_PAGE_HEIGHT - CRAM_MARGIN;
  };

  const ensureSpace = (needed: number) => {
    if (cursorY - needed < CRAM_MARGIN) newPage();
  };

  // Draws a run of wrapped text at the current cursor and advances cursorY.
  const drawText = (
    text: string,
    {
      x = CRAM_MARGIN,
      size = 10,
      color = BLACK,
      bold = false,
      maxWidth = CRAM_CONTENT_WIDTH,
      gap = 4,
    }: {
      x?: number;
      size?: number;
      color?: ReturnType<typeof rgb>;
      bold?: boolean;
      maxWidth?: number;
      gap?: number;
    } = {},
  ) => {
    const lineHeight = size * 1.35;
    const lines = wrapText(text, font, size, maxWidth);
    ensureSpace(lines.length * lineHeight + gap);
    lines.forEach((line) => {
      cursorY -= lineHeight;
      page.drawText(line, { x, y: cursorY, size, font, color });
      if (bold) page.drawText(line, { x: x + 0.3, y: cursorY, size, font, color });
    });
    cursorY -= gap;
  };

  const drawDivider = () => {
    ensureSpace(10);
    cursorY -= 6;
    page.drawLine({
      start: { x: CRAM_MARGIN, y: cursorY },
      end: { x: CRAM_MARGIN + CRAM_CONTENT_WIDTH, y: cursorY },
      thickness: 1,
      color: LIGHT_LINE,
    });
    cursorY -= 8;
  };

  pdfDoc.setTitle(`CRAM Risk Assessment - ${normalizeText(kyc?.name_en) || normalizeText(customer.name_en) || "-"}`);
  pdfDoc.setSubject("CRAM risk assessment & calculation breakdown");
  pdfDoc.setCreationDate(new Date());

  // --- Title ---------------------------------------------------------------
  drawText("CRAM Risk Assessment", { size: 20, bold: true, gap: 10 });

  // --- Header info + photo -------------------------------------------------
  const headerTop = cursorY;
  // Compute the photo dimensions up-front so the divider can be placed below
  // it (otherwise a tall photo overlaps the "CRAM Summary" divider line).
  let photoW = 0;
  let photoH = 0;
  if (photo) {
    const scale = Math.min(96 / photo.width, 120 / photo.height);
    photoW = photo.width * scale;
    photoH = photo.height * scale;
  }
  const infoMaxWidth = CRAM_CONTENT_WIDTH - (photo ? photoW + 16 : 0);
  // The CRAM PDF's "Risk Level" reflects the CRAM rating (when present); falls
  // back to the legacy code. The legacy "Risk Override Reason" line is
  // intentionally omitted from the CRAM PDF (CRAM is the source of truth).
  // A CRAM-team override replaces the headline rating. The engine's own
  // rating is not dropped — it is shown on its own line below, alongside who
  // overrode it and why, so the document records both the decision and what
  // it replaced.
  const override = kyc?.cramOverride;
  const effectiveRating = override?.rating || cram?.rating;
  const displayRiskLevel = titleCase(effectiveRating || riskCode || riskLabel);
  const displayRiskColor = getRiskColor(effectiveRating || riskCode || riskLabel);
  const infoRows: { label: string; value: string; color?: ReturnType<typeof rgb> }[] = [
    { label: "Customer Name:", value: normalizeText(kyc?.name_en) || normalizeText(customer.name_en) || "-" },
    { label: "CIF Number:", value: normalizeText(customer.crmID) || normalizeText(customer.id) || "-" },
    { label: "Printed on:", value: formatRiskRatingPdfDate(printedOn) },
    { label: "Risk Level:", value: displayRiskLevel, color: displayRiskColor },
  ];
  if (override) {
    infoRows.push(
      { label: "Overridden from:", value: titleCase(override.fromRating), color: GREY },
      { label: "Overridden by:", value: normalizeText(override.byName) || normalizeText(override.byEmail) || "-", color: GREY },
      { label: "Override reason:", value: normalizeText(override.reason) || "-", color: GREY },
    );
  }

  infoRows.forEach((row) => {
    const size = 11;
    const lineHeight = size * 1.35;
    const labelWidth = 130;
    const valueLines = wrapText(row.value, font, size, infoMaxWidth - labelWidth);
    ensureSpace(Math.max(1, valueLines.length) * lineHeight + 4);
    const rowStartY = cursorY;
    page.drawText(row.label, { x: CRAM_MARGIN, y: rowStartY - lineHeight, size, font, color: BLACK });
    page.drawText(row.label, { x: CRAM_MARGIN + 0.3, y: rowStartY - lineHeight, size, font, color: BLACK });
    valueLines.forEach((line, i) => {
      page.drawText(line, {
        x: CRAM_MARGIN + labelWidth,
        y: rowStartY - lineHeight * (i + 1),
        size,
        font,
        color: row.color || BLACK,
      });
    });
    cursorY = rowStartY - lineHeight * Math.max(1, valueLines.length) - 4;
  });

  if (photo) {
    page.drawImage(photo, {
      x: CRAM_MARGIN + CRAM_CONTENT_WIDTH - photoW,
      y: headerTop - photoH,
      width: photoW,
      height: photoH,
    });
    // Push the cursor below the photo so the divider never crosses it.
    cursorY = Math.min(cursorY, headerTop - photoH - 8);
  }

  drawDivider();

  if (!cram) {
    drawText("No CRAM assessment is available for this KYC.", { size: 11, color: GREY });
    return pdfDoc.save();
  }

  // --- CRAM summary --------------------------------------------------------
  // Rating is intentionally not repeated here — it is already shown as
  // "Risk Level" in the header above.
  drawText("CRAM Summary", { size: 14, bold: true, gap: 6 });
  if (override) {
    // The score and breakdown below describe the engine's calculation, which
    // keeps running under the override. Saying so prevents the reader from
    // trying to reconcile them with the headline rating.
    drawText(
      `The rating above was set manually by the CRAM team. The engine rating is ${titleCase(cram.rating)}; the score and breakdown below are the engine's.`,
      { size: 10, color: GREY },
    );
  }
  drawText(`Score: ${cram.score}`, { size: 11 });
  if (cram.breakdown) {
    drawText(
      `Score (weighted sum): ${cram.breakdown.weightedSum}   |   Pre-rule rating: ${cram.breakdown.preRuleRating}   |   Total weight: ${cram.breakdown.totalWeight}`,
      { size: 10, color: GREY },
    );
  }

  drawDivider();

  // --- Breakdown table -----------------------------------------------------
  drawText("Calculation Breakdown", { size: 14, bold: true, gap: 6 });

  const cols = [
    { key: "category", title: "Category", width: 110 },
    { key: "input", title: "Input", width: 235 },
    { key: "score", title: "Score", width: 45 },
    { key: "weighted", title: "Weighted", width: 55 },
    { key: "status", title: "Status", width: CRAM_CONTENT_WIDTH - 110 - 235 - 45 - 55 },
  ];
  const cellPad = 4;
  const rowFontSize = 9;
  const rowLineHeight = rowFontSize * 1.3;

  // A cell is a list of logical lines (one per candidate); each is then
  // wrapped to the column width, and the row takes the height of the tallest
  // cell. Candidate lists make these rows genuinely variable-height, so a row
  // can no longer be assumed to fit on the current page.
  const drawTableRow = (
    cells: string[][],
    opts: { header?: boolean; color?: ReturnType<typeof rgb> } = {},
  ) => {
    const wrapped = cells.map((lines, i) =>
      lines.flatMap((text) =>
        wrapText(text, font, rowFontSize, cols[i].width - cellPad * 2),
      ),
    );
    const maxLines = Math.max(...wrapped.map((w) => w.length), 1);
    const rowHeight = maxLines * rowLineHeight + cellPad * 2;
    // Repeat the header when a row spills onto a new page, so a table
    // continuing overleaf is still readable. Guarded on `opts.header` so the
    // header itself cannot recurse.
    if (!opts.header && cursorY - rowHeight < CRAM_MARGIN) {
      newPage();
      drawTableHeader();
    }
    ensureSpace(rowHeight);
    const rowTop = cursorY;
    const rowBottom = rowTop - rowHeight;

    let x = CRAM_MARGIN;
    // Cell borders.
    cols.forEach((col) => {
      page.drawRectangle({
        x,
        y: rowBottom,
        width: col.width,
        height: rowHeight,
        borderWidth: 0.5,
        borderColor: LIGHT_LINE,
      });
      x += col.width;
    });

    x = CRAM_MARGIN;
    wrapped.forEach((lines, i) => {
      lines.forEach((line, li) => {
        page.drawText(line, {
          x: x + cellPad,
          y: rowTop - cellPad - rowFontSize - li * rowLineHeight,
          size: rowFontSize,
          font,
          color: opts.color || BLACK,
        });
        if (opts.header) {
          page.drawText(line, {
            x: x + cellPad + 0.3,
            y: rowTop - cellPad - rowFontSize - li * rowLineHeight,
            size: rowFontSize,
            font,
            color: opts.color || BLACK,
          });
        }
      });
      x += cols[i].width;
    });

    cursorY = rowBottom;
  };

  const drawTableHeader = () => {
    drawTableRow(cols.map((c) => [c.title]), { header: true });
  };

  drawTableHeader();
  breakdownRows.forEach((row) => {
    const statusColor =
      row.status === "missing" || (row.category === "directHigh" && row.status === "used")
        ? rgb(0.88, 0.05, 0)
        : row.status === "unscored"
          ? rgb(0.72, 0.53, 0)
          : BLACK;
    drawTableRow(
      [
        [`${row.label}${row.mandatory ? " *" : ""}`],
        cramRowInputLines(row),
        [`${row.score}`],
        [`${row.weighted}`],
        [cramStatusText(row)],
      ],
      { color: statusColor },
    );
  });

  cursorY -= 8;

  // --- Reason lists --------------------------------------------------------
  const drawReasonList = (title: string, items: string[], color: ReturnType<typeof rgb>) => {
    if (!items || items.length === 0) return;
    drawDivider();
    drawText(title, { size: 12, bold: true, gap: 4 });
    items.forEach((item) => {
      drawText(`• ${item}`, { size: 10, color, x: CRAM_MARGIN + 6, maxWidth: CRAM_CONTENT_WIDTH - 6, gap: 2 });
    });
  };

  drawReasonList("Direct high-risk reasons", cram.directHighReasons || [], rgb(0.88, 0.05, 0));
  drawReasonList("Prohibited reasons", cram.prohibitedReasons || [], rgb(0.88, 0.05, 0));
  drawReasonList("FATF hits", cram.fatfHits || [], rgb(0.85, 0.44, 0));

  return pdfDoc.save();
};

export const openCramRiskPdf = async (
  input: CramRiskPdfInput,
  previewWindow?: Window | null,
): Promise<string> => {
  const pdfBytes = await generateCramRiskPdf(input);
  const pdfArrayBuffer = new ArrayBuffer(pdfBytes.byteLength);
  new Uint8Array(pdfArrayBuffer).set(pdfBytes);
  const pdfUrl = URL.createObjectURL(new Blob([pdfArrayBuffer], { type: "application/pdf" }));

  if (previewWindow && !previewWindow.closed) {
    previewWindow.location.href = pdfUrl;
  } else {
    window.open(pdfUrl, "_blank", "noopener,noreferrer");
  }

  window.setTimeout(() => URL.revokeObjectURL(pdfUrl), 60_000);
  return pdfUrl;
};
